import { useCallback, useEffect, useRef, useState } from "react";
import { REVEAL_MS, RETRACT_MS, type RevealStage } from "@/lib/reveal-motion";

type RevealState = { stage: RevealStage; mounted: boolean };

/** Keeps the last content mounted through its retract, including when hidden. */
export function useRevealStage(
  visible: boolean,
  { onExited }: { onExited?: () => void } = {},
): RevealState & { restart: () => void } {
  const [state, setState] = useState<RevealState>({
    stage: "dot",
    mounted: visible,
  });
  const [restartIndex, setRestartIndex] = useState(0);
  const previousRestart = useRef(restartIndex);
  const mounted = useRef(visible);
  const onExitedRef = useRef(onExited);
  onExitedRef.current = onExited;

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const frames: number[] = [];
    const later = (callback: () => void, ms: number) => {
      timers.push(setTimeout(callback, ms));
    };
    const frame = (callback: () => void) => {
      frames.push(requestAnimationFrame(callback));
    };
    const reveal = () => {
      mounted.current = true;
      setState({ stage: "dot", mounted: true });
      let opened = false;
      const open = () => {
        if (opened) return;
        opened = true;
        setState({ stage: "open", mounted: true });
        later(() => setState({ stage: "idle", mounted: true }), REVEAL_MS);
      };
      // A transparent desktop webview can pause animation frames while its
      // window is inactive. The timer prevents the pill staying a dot.
      later(open, 80);
      // Two frames make the six-pixel dot observable when frames are running.
      frame(() =>
        frame(() => {
          open();
        }),
      );
    };

    const restarting = previousRestart.current !== restartIndex;
    previousRestart.current = restartIndex;
    if (visible) {
      if (restarting && mounted.current) {
        setState({ stage: "exit", mounted: true });
        later(reveal, RETRACT_MS);
      } else {
        reveal();
      }
    } else if (mounted.current) {
      setState({ stage: "exit", mounted: true });
      later(() => {
        mounted.current = false;
        setState({ stage: "dot", mounted: false });
        onExitedRef.current?.();
      }, RETRACT_MS);
    }

    return () => {
      for (const timer of timers) clearTimeout(timer);
      for (const id of frames) cancelAnimationFrame(id);
    };
  }, [visible, restartIndex]);

  const restart = useCallback(() => setRestartIndex((index) => index + 1), []);
  return { ...state, restart };
}
